/* eslint-disable react-hooks/rules-of-hooks, react-hooks/exhaustive-deps */
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { API_BASE, getAuthHeaders } from "../../api/client";
import { LoadingPulse } from "../../ui";
import { schedulingGroupFor, roleFromJobTitle } from "../../lib/roles";
import { rosterScheduleKey } from "../../employeeMatch";
import { hoursBetweenHhmm, timelineBarGeometry } from "../../shiftTimeUtils";
import { logEventDescription, SLOT_LOG_EVENT } from "../../lib/softoneSlotSync";

import {
  SPECIAL_STATUSES,
  specialStatusDisplay,
  resolveEmployeeName,
  COVERAGE_HEATMAP_TOOLTIP_BG,
  dailyTargetStaffingState,
  EmployeeWarningRichTooltip,
} from "./scheduleHelpers";

/** Collapsed bar for schedule builder sections (CHANGES / SHIFTIQ / COVERAGE). */
export function ScheduleSectionCollapsedHeader({ badge, summary, actionLabel, onExpand, style }) {
  const expand = () => onExpand?.();
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={expand}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          expand();
        }
      }}
      style={{
        marginTop: 16,
        background: "var(--surface2)",
        border: "1.5px solid var(--border)",
        borderRadius: 10,
        padding: "8px 14px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
        flexWrap: "wrap",
        width: "100%",
        boxSizing: "border-box",
        cursor: "pointer",
        ...style,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        <span
          style={{
            background: "var(--header-dark)",
            color: "#fff",
            fontSize: 9,
            fontWeight: 800,
            padding: "2px 7px",
            borderRadius: 4,
            letterSpacing: 1,
            flexShrink: 0,
          }}
        >
          {badge}
        </span>
        <span
          style={{
            fontSize: 12,
            color: "var(--text-secondary)",
            fontWeight: 600,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {summary}
        </span>
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          expand();
        }}
        style={{
          fontSize: 11,
          color: "var(--green)",
          fontWeight: 700,
          background: "none",
          border: "none",
          cursor: "pointer",
          flexShrink: 0,
        }}
      >
        {actionLabel}
      </button>
    </div>
  );
}

export function DailyStaffingPanel({ weeklyTargets, weeklyTargetsGeneratedAt, shiftiqLoadError, lang, weekDates, loading, expanded, setExpanded, scheduledByDayIndex, openingHours, dayNames }) {
  const dayNamesGr = ["Δευ", "Τρί", "Τετ", "Πέμ", "Παρ", "Σάβ", "Κυρ"];
  const todayIso = new Date().toISOString().slice(0, 10);

  const formatDateLabel = (iso) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(lang === "gr" ? "el-GR" : "en-GB", { day: "numeric", month: "short" });
  };

  const displayDayName = (day, index) => {
    if (lang === "gr") return dayNamesGr[index] || day;
    return day || ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][index];
  };

  /** Peak row for collapsed bar: same fields as day cards — max peak_bowls_per_hour, tie-break total_daily_slots */
  let collapsedPeakSummary = "";
  if (weeklyTargets && weeklyTargets.length > 0) {
    let bestIdx = 0;
    let bestOrders = -Infinity;
    let bestSlots = -Infinity;
    weeklyTargets.forEach((day, index) => {
      const o = Number(day?.peak_bowls_per_hour);
      const s = Number(day?.total_daily_slots);
      const ov = Number.isFinite(o) ? o : 0;
      const sv = Number.isFinite(s) ? s : 0;
      if (ov > bestOrders || (ov === bestOrders && sv > bestSlots)) {
        bestOrders = ov;
        bestSlots = sv;
        bestIdx = index;
      }
    });
    const day = weeklyTargets[bestIdx];
    const fromWeekDates = weekDates?.[bestIdx];
    const fallbackName = fromWeekDates
      ? ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][
          fromWeekDates.getDay() === 0 ? 6 : fromWeekDates.getDay() - 1
        ]
      : null;
    const dn = displayDayName(day?.day_name || fallbackName, bestIdx);
    const dl = formatDateLabel(day?.date);
    const peakSlots = day?.total_daily_slots ?? "—";
    const peakOrders = day?.peak_bowls_per_hour ?? "—";
    const peakDay = `${dn} ${dl}`.trim();
    collapsedPeakSummary =
      lang === "gr"
        ? `Αιχμή: ${peakDay} · ${peakSlots} slots · ${peakOrders} orders/hr`
        : `Peak: ${peakDay} · ${peakSlots} slots · ${peakOrders} orders/hr`;
  } else if (loading) {
    collapsedPeakSummary = lang === "gr" ? "Φόρτωση…" : "Loading…";
  } else {
    collapsedPeakSummary =
      lang === "gr"
        ? "Αιχμή: — · — slots · — orders/hr"
        : "Peak: — · — slots · — orders/hr";
  }

  if (!expanded) {
    return (
      <ScheduleSectionCollapsedHeader
        badge="SHIFTIQ"
        summary={collapsedPeakSummary}
        actionLabel={lang === "gr" ? "Εμφάνιση στόχων ▾" : "Show targets ▾"}
        onExpand={() => setExpanded(true)}
      />
    );
  }

  if (!weeklyTargets) {
    return (
      <div style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 16,
        padding: "18px 20px",
        display: "flex",
        alignItems: "center",
        gap: 12,
        marginBottom: 16,
        marginTop: 10,
      }}>
        <div style={{ fontWeight: 700, fontSize: 12,
          color: "var(--text-muted)" }}>
          SHIFTIQ
        </div>
        {shiftiqLoadError && (
          <div style={{ marginLeft: 8, padding: "6px 10px", borderRadius: 8, background: "#FEF3C7", color: "#92400E", fontSize: 12, fontWeight: 600 }}>
            ShiftIQ data unavailable — the forecast may not have run yet for this week.
          </div>
        )}
        {loading
          ? <LoadingPulse size={20} />
          : <span style={{ fontSize: 12,
              color: "var(--text-muted)" }}>
              ShiftIQ weekly data unavailable
            </span>
        }
        <button
          type="button"
          onClick={() => setExpanded(false)}
          style={{ marginLeft: "auto", border: "none", background: "transparent", cursor: "pointer", fontSize: 12, color: "var(--green)", fontWeight: 700, padding: "4px 8px" }}
        >
          {lang === "gr" ? "Κλείσιμο" : "Close"}
        </button>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 10, marginBottom: 16, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 16, overflow: "hidden" }}>
      <div
        onClick={() => setExpanded(false)}
        style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", flexWrap: "wrap", cursor: "pointer" }}
      >
        <span style={{ background: "var(--header-dark)", color: "#fff", fontSize: 9, fontWeight: 800, padding: "2px 7px", borderRadius: 4, letterSpacing: 1 }}>SHIFTIQ</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text-primary)" }}>
          {lang === "gr" ? "Ημερήσιοι στόχοι στελέχωσης" : "Daily Staffing Targets"}
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(false);
          }}
          style={{ marginLeft: "auto", border: "none", background: "transparent", cursor: "pointer", fontSize: 12, color: "var(--green)", fontWeight: 700, padding: "4px 8px" }}
          aria-expanded={expanded}
        >
          Hide ▴
        </button>
      </div>
      <div style={{ padding: "0 14px 14px" }}>
        {shiftiqLoadError && (
          <div style={{ marginBottom: 10, padding: "8px 10px", borderRadius: 8, background: "#FEF3C7", color: "#92400E", fontSize: 12, fontWeight: 600 }}>
            ShiftIQ data unavailable — the forecast may not have run yet for this week.
          </div>
        )}
        {weeklyTargets && (
          <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4 }}>
            {weeklyTargets.map((day, index) => {
              const isToday = day?.date === todayIso;
              const fromWeekDates = weekDates?.[index];
              const fallbackName = fromWeekDates ? ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][fromWeekDates.getDay() === 0 ? 6 : fromWeekDates.getDay() - 1] : null;
              const targetSlots = Number(day?.total_daily_slots);
              const scheduledTotal = Number(scheduledByDayIndex?.[index] ?? 0);
              const dayName = dayNames?.[index];
              const dayHours = dayName ? openingHours?.[dayName] : null;
              const dayClosed = !dayHours || dayHours === "CLOSED";
              const noTargetData = !Number.isFinite(targetSlots);
              const staffingState = dailyTargetStaffingState(scheduledTotal, targetSlots, dayClosed, noTargetData);
              return (
                <div
                  key={`${day?.date || index}`}
                  style={{
                    border: `1px solid ${isToday ? "var(--green)" : "var(--border)"}`,
                    borderLeft: `4px solid ${staffingState.border || "var(--border)"}`,
                    borderRadius: 12,
                    padding: "10px 8px",
                    background: isToday ? "var(--green-light)" : "var(--surface)",
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: "var(--text-primary)" }}>
                      {displayDayName(day?.day_name || fallbackName, index)}
                    </div>
                    <div style={{ fontSize: 10, color: "var(--text-muted)" }}>{formatDateLabel(day?.date)}</div>
                  </div>

                  {[
                    ["SUP", day?.supervisor],
                    ["SVC", `${day?.service_crew_lower ?? "—"}–${day?.service_crew_upper ?? "—"}`],
                    ["KIT", day?.kitchen_crew],
                    ["CLN", day?.cleaning_crew],
                  ].map(([label, value]) => (
                    <div key={label} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                      <span style={{ minWidth: 28, fontSize: 8, textTransform: "uppercase", color: "var(--text-muted)", letterSpacing: 0.4 }}>{label}</span>
                      <span style={{ fontSize: 11, fontWeight: 900, color: "var(--text-primary)" }}>{value ?? "—"}</span>
                    </div>
                  ))}

                  <div style={{ marginTop: 7, fontSize: 9, color: "var(--text-muted)" }}>
                    Peak {day?.peak_hour ?? "—"}:00 · {day?.peak_bowls_per_hour ?? "—"} orders/hr
                  </div>

                  <div style={{ marginTop: 7, display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ background: "var(--surface2)", color: "var(--text-secondary)", fontSize: 9, fontWeight: 700, borderRadius: 20, padding: "2px 7px" }}>
                      {day?.total_daily_slots ?? "—"} slots
                    </span>
                    {day?.plan_b_used && (
                      <span style={{ background: "#FEF3C7", color: "#92400E", fontSize: 9, fontWeight: 700, borderRadius: 20, padding: "2px 7px" }}>
                        Plan B
                      </span>
                    )}
                  </div>
                  {staffingState.pill && (
                    <div style={{ marginTop: 6 }}>
                      <span style={{ background: staffingState.pillBg, color: staffingState.pillColor, fontSize: 10, fontWeight: 700, borderRadius: 20, padding: "2px 8px", display: "inline-flex", alignItems: "center" }}>
                        {staffingState.pill}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {weeklyTargetsGeneratedAt && !Number.isNaN(new Date(weeklyTargetsGeneratedAt).getTime()) && (
          <div style={{ marginTop: 8, fontSize: 11, color: "var(--text-muted)" }}>
            Last updated: {new Date(weeklyTargetsGeneratedAt).toLocaleString()}
          </div>
        )}
      </div>
    </div>
  );
}

const SCHEDULE_NAME_COL_DEFAULT = 160;
const SCHEDULE_NAME_COL_MIN = 120;
const SCHEDULE_NAME_COL_MAX = 420;

export function scheduleBuilderTableColGroup(weekDates, nameColPx = SCHEDULE_NAME_COL_DEFAULT) {
  return (
    <colgroup>
      <col style={{ width: nameColPx, minWidth: nameColPx, maxWidth: nameColPx }} />
      {weekDates.map((_, i) => <col key={i} />)}
    </colgroup>
  );
}

export function scheduleBuilderNameColWidthFromStorage() {
  try {
    const saved = localStorage.getItem("pg_schedule_name_col_width");
    const parsed = saved ? parseInt(saved, 10) : null;
    if (parsed && parsed >= SCHEDULE_NAME_COL_MIN && parsed <= SCHEDULE_NAME_COL_MAX) return parsed;
  } catch { /* ignore */ }
  return SCHEDULE_NAME_COL_DEFAULT;
}

export function formatPeopleCostRevenueEuros(amount) {
  const n = Math.round(Number(amount));
  return `€${n.toLocaleString("en-US")}`;
}

export function peopleCostRevenueHintText(basis, amount, lang) {
  if (basis == null || amount == null || !Number.isFinite(Number(amount))) return null;
  const euros = formatPeopleCostRevenueEuros(amount);
  if (basis === "actuals_last_year") {
    return lang === "gr"
      ? `βάση ${euros} πέρυσι`
      : `based on ${euros} last year actuals`;
  }
  if (basis === "actuals") {
    return lang === "gr"
      ? `βάση ${euros} πραγματικά`
      : `based on ${euros} actuals`;
  }
  return lang === "gr"
    ? `βάση ${euros} πρόβλεψη`
    : `based on ${euros} forecast`;
}

export function dayHeaderRevenueBasisLabel(basis, lang) {
  if (!basis) return null;
  if (basis === "actuals" || basis === "actuals_last_year") {
    return lang === "gr" ? "πραγματικά" : "actuals";
  }
  if (basis === "forecast") {
    return lang === "gr" ? "πρόβλεψη" : "forecast";
  }
  return null;
}

export function ScheduleBuilderDayHeaderMetrics({ dayDate, peopleCost, pcLoading, lang, peopleCostPct = undefined }) {
  if (pcLoading) {
    return (
      <div
        style={{
          fontSize: 11,
          marginTop: 4,
          color: "rgba(255,255,255,0.4)",
          fontWeight: 500,
          lineHeight: 1.3,
        }}
      >
        <span style={{ display: "inline-block", minWidth: 40, opacity: 0.65 }}>—</span>
        <span style={{ margin: "0 4px" }}>·</span>
        <span style={{ display: "inline-block", minWidth: 24, opacity: 0.65 }}>—</span>
      </div>
    );
  }

  const day = peopleCost?.daily_breakdown?.[dayDate];
  const revNum = day?.revenue_amount != null ? Number(day.revenue_amount) : NaN;
  const hasRev = Number.isFinite(revNum) && revNum > 0;
  const basisLabel = dayHeaderRevenueBasisLabel(day?.revenue_basis, lang);
  const rawPct = peopleCostPct !== undefined ? peopleCostPct : day?.people_cost_pct;
  const pctNum = rawPct != null ? Number(rawPct) : NaN;
  const hasPct = Number.isFinite(pctNum);

  if (!hasRev && !hasPct) {
    return (
      <div
        style={{
          fontSize: 11,
          marginTop: 4,
          color: "rgba(255,255,255,0.55)",
          fontWeight: 500,
          lineHeight: 1.3,
        }}
      >
        — · —
      </div>
    );
  }

  return (
    <div
      style={{
        fontSize: 11,
        marginTop: 4,
        color: "rgba(255,255,255,0.75)",
        fontWeight: 500,
        lineHeight: 1.35,
      }}
    >
      {hasRev ? (
        <>
          <span>{formatPeopleCostRevenueEuros(revNum)}</span>
          {basisLabel ? (
            <span
              style={{
                fontStyle: "italic",
                color: "rgba(255,255,255,0.5)",
                marginLeft: 4,
                fontWeight: 400,
              }}
            >
              {basisLabel}
            </span>
          ) : null}
        </>
      ) : (
        <span>—</span>
      )}
      <span style={{ margin: "0 3px", color: "rgba(255,255,255,0.45)" }}>·</span>
      <span>{hasPct ? `${pctNum.toFixed(1)}%` : "—"}</span>
    </div>
  );
}

export function PeopleCostRevenueHint({ basis, amount, lang, style }) {
  const text = peopleCostRevenueHintText(basis, amount, lang);
  if (!text) return null;
  const badge = (
    <span
      aria-label={text}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        marginLeft: 3,
        width: 12,
        height: 12,
        borderRadius: "50%",
        fontSize: 9,
        fontWeight: 800,
        color: "var(--text-muted)",
        background: "var(--surface2)",
        border: "1px solid var(--border)",
        cursor: "help",
        verticalAlign: "middle",
        lineHeight: 1,
        flexShrink: 0,
        ...style,
      }}
    >
      ?
    </span>
  );
  return (
    <EmployeeWarningBadgePortalTooltip badge={badge}>
      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.75)", lineHeight: 1.5, whiteSpace: "nowrap" }}>
        {text}
      </div>
    </EmployeeWarningBadgePortalTooltip>
  );
}

export function ScheduleBuilderPeopleCostTheadRow({
  user,
  lang,
  weekDates,
  builderPeopleCostPctByDay,
  builderPeopleCostRevenueByDay = [],
}) {
  const [peopleCostTooltip, setPeopleCostTooltip] = useState(null);

  return (
    <tr>
      <th
        scope="row"
        style={{
          background: "var(--surface2)",
          color: "var(--text-primary)",
          fontSize: 13,
          fontWeight: 800,
          padding: "10px 14px",
          textAlign: "left",
          borderBottom: "1px solid var(--border)",
        }}
      >
        {lang === "gr" ? "Κόστος προσωπικού" : "People Cost"}
      </th>
      {weekDates.map((_, i) => {
        const pct = builderPeopleCostPctByDay[i];
        const revMeta = builderPeopleCostRevenueByDay[i] || {};
        const revHint = peopleCostRevenueHintText(revMeta.basis, revMeta.amount, lang);
        const showMgrDot = ["store_manager", "manager"].includes(user.role);
        const showExactPct = !showMgrDot;
        let pctColor = "var(--text-muted)";
        if (pct != null && Number.isFinite(pct)) {
          if (pct < 22) pctColor = "#16A34A";
          else if (pct <= 30) pctColor = "#F59E0B";
          else pctColor = "#EF4444";
        }
        const mgrDotColor = (pct == null || !Number.isFinite(pct))
          ? "#D1D5DB"
          : (pct < 22 ? "#16A34A" : "#EF4444");
        const statusLabel = (pct == null || !Number.isFinite(pct))
          ? (lang === "gr" ? "Κοντά στο όριο" : "Near limit")
          : (pct < 22
            ? (lang === "gr" ? "Εντός στόχου" : "Within target")
            : (pct <= 30
              ? (lang === "gr" ? "Κοντά στο όριο" : "Near limit")
              : (lang === "gr" ? "Υπέρβαση στόχου" : "Over target")));
        const statusColor = (pct == null || !Number.isFinite(pct))
          ? "#F59E0B"
          : (pct < 22 ? "#4ADE80" : (pct <= 30 ? "#F59E0B" : "#F87171"));
        const valueLabel = null;
        return (
          <th
            key={i}
            scope="col"
            style={{
              background: "var(--surface2)",
              borderLeft: "1px solid var(--border)",
              borderBottom: "1px solid var(--border)",
              padding: "10px 8px",
              textAlign: "center",
            }}
          >
            {showExactPct && (
              <span
                style={{
                  fontSize: 16,
                  fontWeight: 800,
                  color: pctColor,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                }}
              >
                {pct != null && Number.isFinite(pct) ? `${pct.toFixed(1)}%` : "—"}
                {pct != null && Number.isFinite(pct) ? (
                  <PeopleCostRevenueHint basis={revMeta.basis} amount={revMeta.amount} lang={lang} />
                ) : null}
              </span>
            )}
            {showMgrDot && (
              <div
                style={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                onMouseEnter={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setPeopleCostTooltip({
                    day: i,
                    x: rect.left + rect.width / 2,
                    y: rect.top,
                  });
                }}
                onMouseLeave={() => setPeopleCostTooltip(null)}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: mgrDotColor,
                    display: "inline-block",
                    margin: "0 auto",
                    flexShrink: 0,
                  }}
                />
                {peopleCostTooltip?.day === i && createPortal(
                  <div
                    style={{
                      position: "fixed",
                      left: peopleCostTooltip.x,
                      top: peopleCostTooltip.y - 8,
                      transform: "translate(-50%, -100%)",
                      zIndex: 9999,
                      pointerEvents: "none",
                      background: "#0D0F2B",
                      color: "#fff",
                      borderRadius: 12,
                      padding: "10px 14px",
                      fontSize: 12,
                      minWidth: 170,
                      boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
                      whiteSpace: "nowrap",
                      textAlign: "left",
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: 11, color: statusColor, marginBottom: valueLabel ? 6 : 0 }}>
                      {statusLabel}
                    </div>
                    {valueLabel ? (
                      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>
                        {valueLabel}
                      </div>
                    ) : null}
                    {revHint ? (
                      <div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", marginTop: valueLabel ? 4 : 6 }}>
                        {revHint}
                      </div>
                    ) : null}
                  </div>,
                  document.body
                )}
              </div>
            )}
          </th>
        );
      })}
    </tr>
  );
}

export function PeopleCostPanel({ data, loading, lang, weekDates }) {
  function toLocalISO(d) {
    const yr = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${yr}-${mo}-${day}`;
  }

  if (loading) return (
    <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 16, padding: "18px 20px", display: "flex", alignItems: "center", gap: 12 }}>
      <div style={{ fontWeight: 700, fontSize: 12, color: "var(--text-muted)" }}>PEOPLE COST</div>
      <LoadingPulse size={20} />
    </div>
  );

  if (!data || data.weekly_cost === 0) return (
    <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 16, padding: "18px 20px" }}>
      <div style={{ fontWeight: 700, fontSize: 12, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 1 }}>
        PEOPLE COST
      </div>
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 6 }}>
        {lang === "gr" ? "Δεν υπάρχουν δεδομένα για αυτή την εβδομάδα" : "No schedule data for this week yet"}
      </div>
    </div>
  );

  const pct = data.people_cost_pct;
  const alert = data.alert;
  const THRESHOLD = typeof data.threshold_pct === "number" ? data.threshold_pct : 22;
  const pctColor = pct === null ? "var(--text-muted)" : pct > THRESHOLD ? "#DC2626" : pct > THRESHOLD * 0.9 ? "#D97706" : "#16A34A";
  const days = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

  return (
    <div style={{ background: "var(--surface)", border: `1.5px solid ${alert ? "#FCA5A5" : "var(--border)"}`, borderRadius: 16, padding: "18px 20px", marginTop: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>
            PEOPLE COST
          </div>
          <div style={{ fontWeight: 900, fontSize: 28, color: pctColor, lineHeight: 1 }}>
            {pct !== null ? `${pct.toFixed(1)}%` : "—"}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
            {lang === "gr" ? "επί του τζίρου" : "of forecasted revenue"} {" · "} <span style={{ fontWeight: 700 }}>target ≤{THRESHOLD}%</span>
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "var(--text-primary)" }}>
            €{data.weekly_cost.toFixed(0)}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
            {lang === "gr" ? "εβδομαδιαίο κόστος" : "weekly labour cost"}
          </div>
          {data.forecasted_revenue && (
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
              / €{data.forecasted_revenue.toFixed(0)} {lang === "gr" ? "πρόβλεψη" : "forecast"}
            </div>
          )}
        </div>
      </div>

      {alert && (
        <div style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 10, padding: "8px 12px", marginBottom: 14, fontSize: 12, color: "#DC2626", fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
          ⚠️ {lang === "gr" ? `People cost ${pct?.toFixed(1)}% — υπερβαίνει τον στόχο ${THRESHOLD}%` : `People cost ${pct?.toFixed(1)}% — exceeds ${THRESHOLD}% target`}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
        {weekDates.map((d, i) => {
          const iso = toLocalISO(d);
          const day = data.daily_breakdown?.[iso];
          const dayCost = day?.cost ?? 0;
          const dayRev = day?.forecasted_revenue ?? 0;
          const dayPct = dayRev > 0 ? (dayCost / dayRev * 100) : 0;
          const barH = Math.min(100, Math.round(dayPct / THRESHOLD * 100));
          const barColor = dayPct > THRESHOLD ? "#DC2626" : dayPct > THRESHOLD * 0.9 ? "#D97706" : "#16A34A";
          return (
            <div key={iso} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: "var(--text-muted)" }}>
                {days[i]}
              </div>
              <div style={{ width: "100%", height: 48, background: "var(--surface2)", borderRadius: 6, position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: `${barH}%`, background: barColor, borderRadius: 6, transition: "height 0.3s ease" }} />
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1.5, background: "rgba(0,0,0,0.15)" }} />
              </div>
              <div style={{ fontSize: 9, color: dayPct > THRESHOLD ? "#DC2626" : "var(--text-muted)", fontWeight: dayPct > THRESHOLD ? 800 : 600 }}>
                {dayRev > 0 ? `${dayPct.toFixed(0)}%` : "–"}
              </div>
            </div>
          );
        })}
      </div>

      {data.used_fallback_rate?.length > 0 && (
        <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 10 }}>
          ⚠️ {data.used_fallback_rate.length} {lang === "gr" ? "υπάλληλοι με εκτιμώμενο ωρομίσθιο (€6.70)" : "employees using estimated rate (€6.70)"}
        </div>
      )}
    </div>
  );
}

export function scheduleLogChangeSilent(body) {
  fetch(`${API_BASE}/schedule/log-change`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  }).catch((err) => console.error("[schedule/log-change]", err));
}

const SCHEDULE_DAY_KEY_TO_SHORT_GR = {
  mon: "Δευ",
  tue: "Τρι",
  wed: "Τετ",
  thu: "Πεμ",
  fri: "Παρ",
  sat: "Σαβ",
  sun: "Κυρ",
};
const SCHEDULE_DAY_KEY_TO_SHORT_EN = {
  mon: "Mon",
  tue: "Tue",
  wed: "Wed",
  thu: "Thu",
  fri: "Fri",
  sat: "Sat",
  sun: "Sun",
};

export function scheduleDayKeyToShort(dayKey, lang) {
  if (dayKey == null || String(dayKey).trim() === "") return "—";
  const k = String(dayKey).trim().toLowerCase().slice(0, 3);
  const map = lang === "gr" ? SCHEDULE_DAY_KEY_TO_SHORT_GR : SCHEDULE_DAY_KEY_TO_SHORT_EN;
  return map[k] || String(dayKey);
}

export function scheduleChangeRowDescription(row, lang) {
  const t = String(row.change_type || "").toLowerCase();
  const knownUpload = Object.values(SLOT_LOG_EVENT).includes(t);
  if (knownUpload && !["assigned", "removed", "modified", "assign", "remove", "modify"].includes(t)) {
    return logEventDescription(row, lang);
  }
  const oldV = row.old_value != null && row.old_value !== "" ? String(row.old_value) : null;
  const newV = row.new_value != null && row.new_value !== "" ? String(row.new_value) : null;
  if (t === "assigned" || t === "assign") return lang === "gr" ? `Ανατέθηκε ${newV || "—"}` : `Assigned ${newV || "—"}`;
  if (t === "removed" || t === "remove") return lang === "gr" ? "Αφαιρέθηκε" : "Removed";
  if (t === "modified" || t === "modify") return lang === "gr" ? `Τροποποιήθηκε ${oldV || "—"} → ${newV || "—"}` : `Modified ${oldV || "—"} → ${newV || "—"}`;
  if (knownUpload) return logEventDescription(row, lang);
  if (newV && !oldV) return lang === "gr" ? `Ανατέθηκε ${newV}` : `Assigned ${newV}`;
  if (oldV && !newV) return lang === "gr" ? "Αφαιρέθηκε" : "Removed";
  return lang === "gr" ? `Τροποποιήθηκε ${oldV || "—"} → ${newV || "—"}` : `Modified ${oldV || "—"} → ${newV || "—"}`;
}

export function formatScheduleChangeTime(changedAt) {
  if (!changedAt) return "—";
  try {
    const d = new Date(changedAt);
    if (Number.isNaN(d.getTime())) return "—";
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  } catch {
    return "—";
  }
}

/** Above sticky schedule chrome, left nav (100), and builder side panel (200–400). */
const SCHEDULE_TOOLTIP_PORTAL_Z = 11000;

export function getScheduleContentInsetLeft() {
  if (typeof document === "undefined") return 8;
  const main = document.querySelector("main.main-area");
  const x = main?.getBoundingClientRect().left;
  return (typeof x === "number" ? x : 0) + 8;
}

export function clampScheduleTooltipLeftEdge(centerX, cardWidthPx, winW, insetLeft) {
  let left = centerX - cardWidthPx / 2;
  return Math.max(insetLeft, Math.min(left, winW - cardWidthPx - 8));
}

/** Rich warning card — portaled to body so it paints above sticky headers & sidebar; X-clamped to main column. */
export function EmployeeWarningBadgePortalTooltip({ badge, children }) {
  const wrapRef = useRef(null);
  const leaveTimer = useRef(null);
  const [open, setOpen] = useState(false);
  const [layoutKey, setLayoutKey] = useState(0);
  const bumpLayout = useCallback(() => setLayoutKey((k) => k + 1), []);

  useEffect(() => {
    if (!open) return undefined;
    const onScroll = () => bumpLayout();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open, bumpLayout]);

  const layout = useMemo(() => {
    if (!open || typeof window === "undefined") return null;
    const el = wrapRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const winW = window.innerWidth;
    const narrow = winW < 768;
    const insetLeft = getScheduleContentInsetLeft();
    const maxW = Math.min(260, narrow ? winW * 0.8 : 260);
    const centerX = rect.left + rect.width / 2;
    const left = clampScheduleTooltipLeftEdge(centerX, maxW, winW, insetLeft);
    const dir = rect.top < 140 ? "down" : "up";
    const triggerCx = rect.left + rect.width / 2;
    const arrowLeft = Math.max(14, Math.min(maxW - 26, triggerCx - left - 6));
    return { rect, dir, left, maxW, narrow, arrowLeft };
  }, [open, layoutKey]);

  const onEnter = () => {
    if (leaveTimer.current) {
      clearTimeout(leaveTimer.current);
      leaveTimer.current = null;
    }
    setOpen(true);
    bumpLayout();
  };
  const onLeave = () => {
    leaveTimer.current = setTimeout(() => setOpen(false), 160);
  };

  const arrowUp = layout && layout.dir === "up";

  const portal =
    open &&
    layout &&
    createPortal(
      <div
        style={{
          position: "fixed",
          left: layout.left,
          width: layout.maxW,
          maxWidth: layout.narrow ? "min(260px, 80vw)" : 260,
          zIndex: SCHEDULE_TOOLTIP_PORTAL_Z,
          boxSizing: "border-box",
          ...(layout.dir === "up"
            ? { top: layout.rect.top - 8, transform: "translateY(calc(-100% - 8px))" }
            : { top: layout.rect.bottom + 8, transform: "none" }),
          background: COVERAGE_HEATMAP_TOOLTIP_BG,
          color: "#fff",
          borderRadius: 12,
          padding: "12px 14px",
          minWidth: 220,
          boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
          fontSize: 12,
          lineHeight: 1.5,
          pointerEvents: "none",
        }}
      >
        {children}
        <div
          aria-hidden
          style={
            arrowUp
              ? {
                  position: "absolute",
                  bottom: -6,
                  left: layout.arrowLeft,
                  width: 0,
                  height: 0,
                  borderLeft: "6px solid transparent",
                  borderRight: "6px solid transparent",
                  borderTop: `6px solid ${COVERAGE_HEATMAP_TOOLTIP_BG}`,
                }
              : {
                  position: "absolute",
                  top: -6,
                  bottom: "auto",
                  left: layout.arrowLeft,
                  width: 0,
                  height: 0,
                  borderLeft: "6px solid transparent",
                  borderRight: "6px solid transparent",
                  borderBottom: `6px solid ${COVERAGE_HEATMAP_TOOLTIP_BG}`,
                  borderTop: "none",
                }
          }
        />
      </div>,
      document.body
    );

  return (
    <div
      ref={wrapRef}
      className="gantt-bar-item employee-warning-badge-portal-anchor"
      style={{ position: "relative", display: "inline-flex", flexShrink: 0, zIndex: 2 }}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      {badge}
      {portal}
    </div>
  );
}

/** Simple one-line tooltips — portaled for same stacking + horizontal clamp. */
export function PersonTimelineTooltipHostDiv({ className, style, onClick, children, tooltip }) {
  const hostRef = useRef(null);
  const leaveTimer = useRef(null);
  const [open, setOpen] = useState(false);
  const [layoutKey, setLayoutKey] = useState(0);
  const bumpLayout = useCallback(() => setLayoutKey((k) => k + 1), []);

  useEffect(() => {
    if (!open) return undefined;
    const onScroll = () => bumpLayout();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open, bumpLayout]);

  const layout = useMemo(() => {
    if (!open || tooltip == null || typeof window === "undefined") return null;
    const el = hostRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const winW = window.innerWidth;
    const narrow = winW < 768;
    const insetLeft = getScheduleContentInsetLeft();
    const maxW = Math.min(300, narrow ? winW * 0.9 : 300);
    const centerX = rect.left + rect.width / 2;
    const minHalf = 48;
    const anchorX = Math.max(insetLeft + minHalf, Math.min(centerX, winW - minHalf - 8));
    const dir = rect.top < 140 ? "down" : "up";
    return { rect, dir, anchorX, maxW, narrow };
  }, [open, layoutKey, tooltip]);

  return (
    <div
      ref={hostRef}
      className={className}
      style={style}
      onClick={onClick}
      onMouseEnter={() => {
        if (leaveTimer.current) {
          clearTimeout(leaveTimer.current);
          leaveTimer.current = null;
        }
        setOpen(true);
        bumpLayout();
      }}
      onMouseLeave={() => {
        leaveTimer.current = setTimeout(() => setOpen(false), 120);
      }}
    >
      {children}
      {open && layout && tooltip != null
        ? createPortal(
            <div
              className="gantt-tooltip"
              style={{
                position: "fixed",
                left: layout.anchorX,
                maxWidth: layout.maxW,
                width: "max-content",
                zIndex: SCHEDULE_TOOLTIP_PORTAL_Z,
                boxSizing: "border-box",
                ...(layout.dir === "up"
                  ? {
                      top: layout.rect.top - 8,
                      transform: "translate(-50%, calc(-100% - 8px))",
                    }
                  : {
                      top: layout.rect.bottom + 8,
                      transform: "translateX(-50%)",
                    }),
                background: "#1A1A1A",
                color: "#fff",
                fontSize: 11,
                fontWeight: 600,
                padding: "6px 10px",
                borderRadius: 8,
                whiteSpace: layout.narrow ? "normal" : "nowrap",
                boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                pointerEvents: "none",
              }}
            >
              {tooltip}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

export function PersonTimelineView({
  staff,
  schedule,
  ganttData,
  store,
  weekStart,
  openTime,
  closeTime,
  lang,
  coverageData,
  hoursPerDay: hoursPerDayProp = null,
  validationStatusByDate = {},
  builderAlerts = [],
  activeDay = null,
  onDayClick = () => {},
  onDayHover = () => {},
  onCellClick = null,
  scheduleReadOnly = false,
}) {
  const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const dayNamesGr = ["Δευ", "Τρί", "Τετ", "Πέμ", "Παρ", "Σάβ", "Κυρ"];
  const roleOrder = ["supervisor", "service", "kitchen", "cleaning"];
  const roleColor = {
    supervisor: "var(--green)",
    service: "#3B82F6",
    kitchen: "#F59E0B",
    cleaning: "#8B5CF6",
  };
  const roleLabel = {
    supervisor: "SUPERVISOR",
    service: "SERVICE",
    kitchen: "KITCHEN",
    cleaning: "CLEANING",
  };
  const toHour = (t) => {
    if (!t) return 0;
    const str = String(t).trim();
    const parts = str.split(":");
    const h = parseInt(parts[0], 10) || 0;
    const m = parseInt(parts[1], 10) || 0;
    return h + m / 60;
  };
  const formatBarTime = (t) => {
    if (!t) return "";
    const parts = String(t).split(":");
    const h = parts[0].padStart(2, "0");
    const m = parts[1] || "00";
    return m === "00" ? h : `${h}:${m}`;
  };
  const CELL_OPEN = toHour(openTime || "08:00");
  const CELL_CLOSE = toHour(closeTime || "22:00");
  const weekStartDate = new Date(`${weekStart}T12:00:00`);
  const weekDates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStartDate);
    d.setDate(weekStartDate.getDate() + i);
    return d;
  });
  const fmtDate = (d) => d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
  const toLocalISO = (d) => {
    const yr = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${yr}-${mo}-${day}`;
  };
  const dayKey = (d, i) => `${dayNames[i]} ${fmtDate(d)}`;
  const countsTowardContractDay = (val) => {
    if (!val || val === "OFF") return false;
    if (val === "ΑΔΕΙΑ") return true;
    return String(val).includes("-");
  };
  const normalizeName = (v) => String(v || "").trim().toLowerCase();
  const hoursBetween = (start, end) => hoursBetweenHhmm(start, end);
  const parseCell = (cell) => {
    if (!cell) return { kind: "empty" };
    if (typeof cell === "string") {
      const val = cell.trim();
      if (!val) return { kind: "empty" };
      const spFromVal = SPECIAL_STATUSES.find((s) => s.value === val);
      if (spFromVal) {
        if (spFromVal.value === "OFF") return { kind: "off" };
        if (spFromVal.value === "ΑΔΕΙΑ") return { kind: "leave" };
        if (spFromVal.value === "ΑΝΑΡΡΩΤΙΚΗ") return { kind: "sick" };
        return { kind: "special", sp: spFromVal };
      }
      if (val === "SICK" || val === "ΑΣΘΕΝΕΙΑ") return { kind: "sick" };
      if (val === "LEAVE") return { kind: "leave" };
      if (val.includes("-")) {
        const m = val.match(/^(\d{1,2}:\d{2}(?::\d{2})?)-(\d{1,2}:\d{2}(?::\d{2})?)$/);
        if (m) {
          const start = m[1];
          const end = m[2];
          return { kind: "shift", shift_start: start.slice(0, 5), shift_end: end.slice(0, 5) };
        }
      }
      return { kind: "empty" };
    }
    const status = String(cell?.status || "").toLowerCase();
    if (status === "off") return { kind: "off" };
    if (status === "no_work") return { kind: "no_work" };
    if (status === "leave" || status === "adeia") return { kind: "leave" };
    if (status === "sick" || status === "astheneia") return { kind: "sick" };
    if (cell?.type === "off") return { kind: "off" };
    if (cell?.shift_start && cell?.shift_end) {
      return { kind: "shift", shift_start: String(cell.shift_start).slice(0, 5), shift_end: String(cell.shift_end).slice(0, 5) };
    }
    return { kind: "empty" };
  };
  const getCellRaw = (employeeId, dayIdx) => {
    const key = dayKey(weekDates[dayIdx], dayIdx);
    return schedule?.[employeeId]?.[key];
  };
  const getContractedDays = (emp) => Number(emp?.work_days ?? emp?.workDays ?? emp?.days_per_week ?? 5);
  const countScheduledShiftDays = (empId) => {
    const empSched = schedule?.[empId] || {};
    return weekDates.reduce((acc, d, i) => {
      const dk = dayKey(d, i);
      return countsTowardContractDay(empSched[dk] || "") ? acc + 1 : acc;
    }, 0);
  };
  const storeStaff = (staff || []).filter((s) => String(s?.store || "") === String(store || ""));
  const timelineStaffList = staff || [];
  const getCellData = (employeeId, dayDate, dayIdx) => {
    const cellRaw = getCellRaw(employeeId, dayIdx);
    const cell = parseCell(cellRaw);
    const emp = staff.find((e) => String(rosterScheduleKey(e)) === String(employeeId));
    const firstName = String(emp?.name || "").trim().split(/\s+/)[0] || "";
    const dayGantt = (ganttData?.days || []).find((d) => String(d?.date || "") === String(dayDate));
    const roleGuess = String(emp?.group || roleFromJobTitle(emp?.job_title || emp?.jobTitle || "") || "").toLowerCase();
    const draftSlot = (dayGantt?.draft_slots || []).find((s) => {
      if (normalizeName(s?.role) !== roleGuess) return false;
      const sc = String(s?.employee_code || "").trim();
      if (sc && String(employeeId).trim() === sc) return true;
      return normalizeName(s?.employee_name).includes(normalizeName(firstName));
    });
    return { cell, draftSlot, dayGantt };
  };
  const hasMissingForRoleOnDay = (role, dayIso) => {
    const dayGantt = (ganttData?.days || []).find((d) => String(d?.date || "") === String(dayIso));
    return (dayGantt?.missing_slots || []).some((s) => normalizeName(s?.role) === role);
  };
  const roleCoverageForDay = (role, dayIso) => {
    const dayGantt = (ganttData?.days || []).find((d) => String(d?.date || "") === String(dayIso));
    const realDraft = (dayGantt?.draft_slots || []).filter((s) => {
      const nm = String(s?.employee_name || "");
      const ec = String(s?.employee_code || "").trim();
      return (
        normalizeName(s?.role) === role &&
        (nm || ec) &&
        nm !== "__gap__" &&
        ec !== "__gap__" &&
        !nm.startsWith("Optimal_") &&
        !nm.startsWith("OPTIMAL_")
      );
    });
    const optimalSlots = (dayGantt?.optimal_slots || []).filter((s) => normalizeName(s?.role) === role);
    const covered_hours = realDraft.reduce((sum, s) => sum + hoursBetween(s?.start, s?.end), 0);
    const optimal_hours = optimalSlots.reduce((sum, s) => sum + hoursBetween(s?.start, s?.end), 0);
    return { covered_hours, optimal_hours };
  };
  const openingHoursByDay = coverageData?.openingHours || {};
  const hoursPerDay = hoursPerDayProp
    ?? dayNames.map((dn) => {
      const raw = openingHoursByDay[dn];
      if (!raw || raw === "CLOSED") return { closed: true };
      const parts = String(raw).split("-");
      if (parts.length < 2) return { closed: true };
      return { open: parts[0].trim(), close: parts[1].trim(), closed: false };
    });

  const TIMELINE_SCHEDULE_HEADER_BG = "var(--header-dark)";
  /** Same first-column width as default schedule table (`scheduleBuilderTableColGroup` nameColPx 160). */
  const PT_NAME_COL_W = 160;

  const dayColumnBgWithFocus = (baseBg, dayIso) => {
    if (!activeDay || activeDay !== dayIso) return baseBg;
    if (baseBg === "transparent") return "rgba(16,185,129,0.03)";
    return `linear-gradient(rgba(16,185,129,0.03), rgba(16,185,129,0.03)), ${baseBg}`;
  };

  return (
    <div style={{ background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: 14, overflow: "visible" }}>
      <style>{`
        .timeline-cell { transition: background 0.1s ease; }
        .timeline-cell:hover { background: var(--green-light) !important; }
        .timeline-empty-idle { opacity: 1; transition: opacity 0.12s ease; }
        .timeline-empty-hover { opacity: 0; transition: opacity 0.12s ease; }
        .timeline-cell:hover .timeline-empty-idle { opacity: 0; }
        .timeline-cell:hover .timeline-empty-hover { opacity: 0.6; }
        .timeline-cell:hover .gantt-tooltip { opacity: 1 !important; }
        .timeline-day-header:hover { opacity: 0.85; }
        .timeline-day-header .day-key-hint { opacity: 0; transition: opacity 0.15s ease; }
        .timeline-day-header:hover .day-key-hint { opacity: 1; }
        .gantt-bar-item:hover .gantt-tooltip { opacity: 1 !important; }
      `}</style>
      <div
        style={{
          position: "sticky",
          top: 52,
          zIndex: 9,
          background: TIMELINE_SCHEDULE_HEADER_BG,
          display: "flex",
          borderBottom: "1px solid rgba(255,255,255,0.12)",
        }}
      >
        <div
          style={{
            width: PT_NAME_COL_W,
            minWidth: PT_NAME_COL_W,
            flexShrink: 0,
            padding: "10px 14px",
            fontSize: 11,
            fontWeight: 800,
            color: "#fff",
            textTransform: "uppercase",
            boxSizing: "border-box",
          }}
        >
          {lang === "gr" ? "ONOM/NYMO" : "NAME/SURNAME"}
        </div>
        {weekDates.map((d, i) => {
          const iso = toLocalISO(d);
          const hp = hoursPerDay[i];
          const hoursClosed = hp?.closed || !hp?.open || !hp?.close;
          const hoursLine = hoursClosed ? "CLOSED" : `${hp.open}–${hp.close}`;
          return (
            <div
              key={`pt-head-${iso}`}
              className="timeline-day-header"
              role="button"
              tabIndex={0}
              title="Click or press D to view day detail"
              onClick={(e) => {
                e.stopPropagation();
                onDayClick(iso);
              }}
              onMouseEnter={() => onDayHover(iso)}
              onMouseLeave={() => onDayHover(null)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onDayClick(iso);
                }
              }}
              style={{
                flex: 1,
                minWidth: 110,
                padding: "8px 6px",
                textAlign: "center",
                borderLeft: "1px solid rgba(255,255,255,0.1)",
                boxSizing: "border-box",
                cursor: "pointer",
                position: "relative",
                background: activeDay === iso ? "#1A1F45" : TIMELINE_SCHEDULE_HEADER_BG,
                borderBottom:
                  validationStatusByDate[iso] === "blocking"
                    ? "2px solid #EF4444"
                    : validationStatusByDate[iso] === "warning"
                      ? "2px solid #F59E0B"
                      : activeDay === iso
                        ? "3px solid var(--green)"
                        : "3px solid transparent",
              }}
            >
              <span
                className="day-key-hint"
                style={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  fontSize: 8,
                  fontWeight: 800,
                  background: "rgba(255,255,255,0.15)",
                  color: "#fff",
                  borderRadius: 3,
                  padding: "1px 4px",
                }}
              >
                D
              </span>
              <div style={{ fontSize: 12, fontWeight: 800, color: "#fff" }}>
                {lang === "gr" ? dayNamesGr[i] : dayNames[i]}, {fmtDate(d)}
              </div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  marginTop: 3,
                  color: hoursClosed ? "rgba(255,255,255,0.5)" : "rgba(255,255,255,0.7)",
                  textAlign: "center",
                }}
              >
                {hoursLine}
              </div>
            </div>
          );
        })}
      </div>
      {roleOrder.map((role) => {
        const roleStaff = storeStaff.filter((emp) => schedulingGroupFor(emp) === role);
        if (!roleStaff.length) return null;
        return (
          <div key={`pt-role-${role}`}>
            <div style={{ background: roleColor[role], padding: "6px 14px", fontSize: 11, fontWeight: 800, color: "#fff", textTransform: "uppercase" }}>
              {roleLabel[role]}
            </div>
            {roleStaff.map((emp) => {
              const rowAlerts = builderAlerts.filter((a) => a.emp === emp.name);
              const schedN = countScheduledShiftDays(rosterScheduleKey(emp));
              const contrN = getContractedDays(emp);
              return (
                <div
                  key={`pt-row-${role}-${emp.id || emp.name}`}
                  style={{
                    display: "flex",
                    borderBottom: "1px solid var(--border)",
                    minHeight: 56,
                    alignItems: "center",
                    background: rowAlerts.some((a) => a.type === "rest") ? "#FFF5F5" : "transparent",
                  }}
                >
                  <div
                    style={{
                      width: PT_NAME_COL_W,
                      minWidth: PT_NAME_COL_W,
                      flexShrink: 0,
                      padding: "8px 14px",
                      fontWeight: 700,
                      color: "var(--text-primary)",
                      background: rowAlerts.length > 0 ? "inherit" : "var(--surface)",
                      borderRight: "1px solid var(--border)",
                      boxSizing: "border-box",
                    }}
                  >
                    <div
                      title={resolveEmployeeName(emp.name, timelineStaffList)}
                      style={{
                        fontSize: 12,
                        maxWidth: PT_NAME_COL_W,
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                        lineHeight: 1.25,
                        fontWeight: 700,
                        color: "var(--text-primary)",
                      }}
                    >
                      {resolveEmployeeName(emp.name, timelineStaffList).toUpperCase()}
                    </div>
                    <div style={{ display: "flex", gap: 4, marginTop: 3, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 9, background: (emp.shiftHours ?? emp.shift_hours ?? 8) === 8 ? "#DCFCE7" : "#EDE9FE", color: (emp.shiftHours ?? emp.shift_hours ?? 8) === 8 ? "#15803D" : "#6D28D9", fontWeight: 700, padding: "1px 6px", borderRadius: 10 }}>
                        {emp.shiftHours ?? emp.shift_hours ?? 8}H
                      </span>
                      <span style={{ fontSize: 9, background: "#FEF9C3", color: "#854D0E", fontWeight: 700, padding: "1px 6px", borderRadius: 10 }}>
                        {emp.workDays ?? emp.days_per_week ?? contrN}D/W
                      </span>
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 800,
                          padding: "1px 6px",
                          borderRadius: 10,
                          background: "var(--surface2)",
                          color:
                            schedN < contrN
                              ? "#15803D"
                              : schedN === contrN
                                ? "#B45309"
                                : "#B91C1C",
                        }}
                      >
                        {`${schedN}/${contrN}`}
                      </span>
                      {rowAlerts.length > 0 ? (
                        <EmployeeWarningBadgePortalTooltip
                          badge={
                            <span style={{ fontSize: 9, background: "#FEE2E2", color: "#991B1B", fontWeight: 700, padding: "1px 6px", borderRadius: 10, cursor: "help" }}>
                              {`⚠ ${rowAlerts.length}`}
                            </span>
                          }
                        >
                          <EmployeeWarningRichTooltip
                            warnings={builderAlerts}
                            emp={emp}
                            lang={lang}
                            openingHours={coverageData?.openingHours || {}}
                          />
                        </EmployeeWarningBadgePortalTooltip>
                      ) : null}
                    </div>
                  </div>
                  {weekDates.map((d, i) => {
                    const dayIso = toLocalISO(d);
                    const { cell, draftSlot } = getCellData(rosterScheduleKey(emp), dayIso, i);
                    const isMissingForRole = cell.kind === "empty" && hasMissingForRoleOnDay(role, dayIso);
                    const baseBg =
                      cell.kind === "off"
                        ? "#FEF3C7"
                        : cell.kind === "leave"
                          ? "var(--green-light)"
                          : cell.kind === "sick"
                            ? "#FEE2E2"
                            : cell.kind === "special"
                              ? `${cell.sp?.color || "#E5E7EB"}33`
                              : isMissingForRole
                                ? "#FFF1F1"
                                : "var(--surface)";
                    const barGeo =
                      cell.kind === "shift"
                        ? timelineBarGeometry(cell.shift_start, cell.shift_end, CELL_OPEN, CELL_CLOSE)
                        : null;
                    const leftPct = barGeo ? barGeo.leftPct : "0%";
                    const barWidthPct = barGeo ? parseFloat(barGeo.widthPct) : 0;
                    const widthPct = barGeo ? barGeo.widthPct : "0%";
                    const shiftHours =
                      cell.kind === "shift"
                        ? hoursBetweenHhmm(cell.shift_start, cell.shift_end)
                        : 0;
                    const renderAsEmpty = cell.kind === "shift" && (Number.isFinite(barWidthPct) ? barWidthPct < 2 : false);
                    const tooltipStatus = draftSlot?.drifts === true ? "Deviates from optimal" : "Matches optimal";
                    const shiftTooltipText =
                      cell.kind === "shift" && !renderAsEmpty
                        ? `${resolveEmployeeName(emp.name, timelineStaffList)} · ${formatBarTime(cell.shift_start)}–${formatBarTime(cell.shift_end)} · ${shiftHours.toFixed(0)}h · ${tooltipStatus}`
                        : null;
                    return (
                      <PersonTimelineTooltipHostDiv
                        key={`pt-cell-${emp.id || emp.name}-${i}`}
                        className="timeline-cell"
                        onClick={() => {
                          if (scheduleReadOnly || typeof onCellClick !== "function") return;
                          onCellClick(emp, dayKey(d, i));
                        }}
                        style={{
                          flex: 1,
                          minWidth: 90,
                          height: 56,
                          position: "relative",
                          borderLeft: "1px solid var(--border)",
                          cursor: scheduleReadOnly ? "default" : "pointer",
                          background: dayColumnBgWithFocus(baseBg, dayIso),
                          outline: isMissingForRole ? "1.5px dashed #EF4444" : "none",
                          outlineOffset: -1,
                        }}
                        tooltip={shiftTooltipText}
                      >
                        {cell.kind === "off" ? (
                          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: "#92400E" }}>
                            {specialStatusDisplay("OFF", lang)}
                          </div>
                        ) : null}
                        {cell.kind === "leave" ? (
                          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: "var(--green)" }}>
                            {specialStatusDisplay("ΑΔΕΙΑ", lang)}
                          </div>
                        ) : null}
                        {cell.kind === "sick" ? (
                          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: "#991B1B" }}>
                            {specialStatusDisplay("ΑΝΑΡΡΩΤΙΚΗ", lang)}
                          </div>
                        ) : null}
                        {cell.kind === "special" && cell.sp ? (
                          <div
                            style={{
                              position: "absolute",
                              inset: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 10,
                              fontWeight: 800,
                              color:
                                cell.sp.textColor === "#FFFFFF" || cell.sp.textColor?.toLowerCase?.() === "#fff"
                                  ? "#1E3A8A"
                                  : cell.sp.textColor,
                            }}
                          >
                            {lang === "gr" ? cell.sp.labelGr : cell.sp.label}
                          </div>
                        ) : null}
                        {cell.kind === "shift" && !renderAsEmpty ? (
                          <>
                            <div
                              style={{
                                position: "absolute",
                                top: 8,
                                height: 28,
                                borderRadius: 4,
                                left: leftPct,
                                width: widthPct,
                                background: roleColor[role],
                                borderLeft: draftSlot?.drifts ? (role === "supervisor" ? "4px solid #F97316" : "3px solid #F97316") : "none",
                                color: "#fff",
                                fontSize: 8,
                                fontWeight: 700,
                                paddingLeft: 4,
                                lineHeight: "28px",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {`${draftSlot?.drifts ? "⚠ " : ""}${formatBarTime(cell.shift_start)}–${formatBarTime(cell.shift_end)}`}
                            </div>
                            <div style={{ position: "absolute", left: 0, right: 0, bottom: 6, fontSize: 8, color: "var(--text-muted)", textAlign: "center" }}>
                              {`${shiftHours.toFixed(0)}h`}
                            </div>
                          </>
                        ) : null}
                        {cell.kind === "empty" && !isMissingForRole ? (
                          <>
                            <span className="timeline-empty-idle" style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", fontSize: 12, color: "var(--border)", fontWeight: 300, pointerEvents: "none" }}>—</span>
                            <span className="timeline-empty-hover" style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", fontSize: 16, color: "var(--green)", pointerEvents: "none" }}>+</span>
                          </>
                        ) : null}
                        {renderAsEmpty ? (
                          <>
                            <span className="timeline-empty-idle" style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", fontSize: 12, color: "var(--border)", fontWeight: 300, pointerEvents: "none" }}>—</span>
                            <span className="timeline-empty-hover" style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", fontSize: 16, color: "var(--green)", pointerEvents: "none" }}>+</span>
                          </>
                        ) : null}
                      </PersonTimelineTooltipHostDiv>
                    );
                  })}
                </div>
              );
            })}
            <div style={{ display: "flex", borderBottom: "1px solid var(--border)" }}>
              <div style={{ width: PT_NAME_COL_W, minWidth: PT_NAME_COL_W, flexShrink: 0, padding: "4px 14px", fontSize: 9, fontWeight: 800, color: "var(--text-muted)" }}>
                COVERAGE
              </div>
              {weekDates.map((d, i) => {
                const dayIso = toLocalISO(d);
                const { covered_hours, optimal_hours } = roleCoverageForDay(role, dayIso);
                const base = Math.max(1, optimal_hours);
                const greenPct = Math.min(100, (Math.min(covered_hours, optimal_hours) / base) * 100);
                const redPct = covered_hours < optimal_hours ? Math.max(0, ((optimal_hours - covered_hours) / base) * 100) : 0;
                const amberPct = covered_hours > optimal_hours ? Math.max(0, ((covered_hours - optimal_hours) / covered_hours) * 100) : 0;
                return (
                  <PersonTimelineTooltipHostDiv
                    key={`pt-cov-${role}-${i}`}
                    style={{ flex: 1, minWidth: 90, height: 10, background: dayColumnBgWithFocus("var(--surface2)", dayIso), borderLeft: "1px solid var(--border)", position: "relative" }}
                    tooltip={`${covered_hours.toFixed(1)}h covered / ${optimal_hours.toFixed(1)}h optimal`}
                  >
                    <div style={{ position: "absolute", inset: 0, display: "flex" }}>
                      <div style={{ width: `${greenPct}%`, height: "100%", background: "rgba(16,185,129,0.7)", borderRadius: 2 }} />
                      {redPct > 0 ? <div style={{ width: `${redPct}%`, height: "100%", background: "rgba(239,68,68,0.7)", borderRadius: 2 }} /> : null}
                      {amberPct > 0 ? <div style={{ width: `${amberPct}%`, height: "100%", background: "rgba(245,158,11,0.7)", borderRadius: 2 }} /> : null}
                    </div>
                  </PersonTimelineTooltipHostDiv>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const SCHEDULE_SERVER_ROLE_COVERAGE_RULE_CODES = new Set([
  "STAFF_OPEN_EARLY",
  "STAFF_CLOSE_LATE",
  "MIN_TWO_OPENING",
  "MIN_TWO_CLOSING",
  "SUPERVISOR_PER_DAY",
  "SUPERVISOR_PEAK",
  "KITCHEN_OPENING",
  "KITCHEN_SECOND_12",
  "CLEANING_CLOSING",
  "CLEANING_SECOND_12",
]);

export function isNoShiftsAssignedClientAlert(al) {
  if (al?.type !== "coverage") return false;
  const msg = String(al.msg || "");
  const msgGr = String(al.msgGr || "");
  return msg.includes("No shifts assigned") || msgGr.includes("Δεν υπάρχουν βάρδιες");
}

export function isServerRoleOrCoverageViolation(v) {
  const code = String(v?.rule_code || "");
  if (SCHEDULE_SERVER_ROLE_COVERAGE_RULE_CODES.has(code)) return true;
  return code.startsWith("role_");
}

export function clientAlertCoversServerViolation(al, v) {
  const code = String(v?.rule_code || "");
  const msg = String(al.msg || "").toLowerCase();
  if (isNoShiftsAssignedClientAlert(al)) {
    return isServerRoleOrCoverageViolation(v);
  }
  if (al.type === "coverage" && msg.includes("opening gap")) {
    return code === "STAFF_OPEN_EARLY" || code.includes("open");
  }
  if (al.type === "coverage" && msg.includes("closing gap")) {
    return code === "STAFF_CLOSE_LATE" || code.includes("close");
  }
  if (al.type === "role" && msg.includes("kitchen") && msg.includes("opening")) {
    return code === "KITCHEN_OPENING" || code === "KITCHEN_SECOND_12" || code.startsWith("role_opening");
  }
  if (al.type === "role" && msg.includes("kitchen") && msg.includes("closing")) {
    return code === "CLEANING_CLOSING" || code === "CLEANING_SECOND_12" || code.startsWith("role_closing");
  }
  if (al.type === "role" && msg.includes("service") && msg.includes("opening")) {
    return code === "MIN_TWO_OPENING" || code === "SUPERVISOR_PEAK";
  }
  if (al.type === "role" && msg.includes("service") && msg.includes("closing")) {
    return code === "MIN_TWO_CLOSING";
  }
  if (al.type === "role" && msg.includes("supervisor")) {
    return code === "SUPERVISOR_PER_DAY" || code === "SUPERVISOR_PEAK";
  }
  const desc = String(v?.description || "").toLowerCase();
  const alCore = msg.replace(/^[^:]+:\s*/, "").slice(0, 48);
  if (alCore.length >= 12 && desc.includes(alCore.slice(0, 24))) return true;
  return false;
}

export function filterServerViolationsForDay(clientDayAlerts, serverDayRaw) {
  const hasNoShifts = clientDayAlerts.some(isNoShiftsAssignedClientAlert);
  return serverDayRaw.filter((v) => {
    if (hasNoShifts && isServerRoleOrCoverageViolation(v)) return false;
    return !clientDayAlerts.some((al) => clientAlertCoversServerViolation(al, v));
  });
}

export function buildScheduleAlertDigest({
  weekDates,
  dayNames,
  dayNamesGr,
  toLocalISO,
  fmtDate,
  criticalAlerts,
  warningAlerts,
  serverViolations,
  showWarnings,
  hasShifts = false,
  lang,
  incompleteShiftEmployeeCount = 0,
}) {
  const dayBlocks = [];
  weekDates.forEach((d, i) => {
    const dateIso = toLocalISO(d);
    const dn = dayNames[i];
    const clientDay = criticalAlerts.filter((a) => a.day === dn);
    const serverFiltered = filterServerViolationsForDay(
      clientDay,
      serverViolations.filter((v) => v.date === dateIso)
    );
    const items = [
      ...clientDay.map((al, idx) => ({
        key: `client-${dateIso}-${idx}`,
        severity: "blocking",
        text: lang === "gr" ? al.msgGr : al.msg,
      })),
      ...serverFiltered.map((v, idx) => ({
        key: `server-${dateIso}-${v.rule_code}-${idx}`,
        severity: v.severity === "blocking" ? "blocking" : "warning",
        text: v.description,
      })),
    ];
    if (items.length === 0) return;
    const criticalCount = items.filter((it) => it.severity === "blocking").length;
    const warningCount = items.filter((it) => it.severity === "warning").length;
    dayBlocks.push({
      kind: "day",
      dateIso,
      label: `${lang === "gr" ? dayNamesGr[i] : dn}, ${fmtDate(d)}`,
      criticalCount,
      warningCount,
      items,
    });
  });

  const employeeRest = criticalAlerts.filter((a) => a.type === "rest");
  if (employeeRest.length > 0) {
    dayBlocks.push({
      kind: "rest",
      dateIso: "__rest__",
      label: lang === "gr" ? "Ανάπαυση μεταξύ βαρδιών" : "Rest between shifts",
      criticalCount: employeeRest.length,
      warningCount: 0,
      items: employeeRest.map((al, idx) => ({
        key: `rest-${idx}`,
        severity: "blocking",
        text: lang === "gr" ? al.msgGr : al.msg,
      })),
    });
  }

  let criticalCount = dayBlocks.reduce((n, b) => n + b.criticalCount, 0);
  let warningCount = dayBlocks.reduce((n, b) => n + b.warningCount, 0);
  const employeeWarnings = showWarnings ? warningAlerts : [];
  const contractMismatchWarningCount = employeeWarnings.filter((a) => a.type === "shifts").length;
  warningCount += contractMismatchWarningCount;
  if (showWarnings) {
    warningCount += incompleteShiftEmployeeCount;
  }

  const dayCount = dayBlocks.filter((b) => b.kind === "day").length;
  const hasIssues = criticalCount + warningCount > 0;

  return {
    dayBlocks,
    employeeWarnings,
    incompleteShiftEmployeeCount,
    criticalCount,
    warningCount,
    totalIssueCount: criticalCount + warningCount,
    dayCount,
    hasIssues,
    hasShifts,
  };
}

export function getTimeShiftCellVisual(statusLower, cellDirty, cellConflicted = false) {
  if (cellConflicted) {
    return { bg: "#FFF7ED", outline: "2px dashed #EA580C", color: "#9A3412" };
  }
  if (statusLower === "published" && cellDirty) {
    return { bg: "#FEF9C3", outline: "2px solid #F59E0B", color: "#92400E" };
  }
  if (statusLower === "published") {
    return { bg: "#D1FAE5", outline: "2px solid #15803D", color: "#065F46" };
  }
  if (statusLower === "draft" && !cellDirty) {
    return { bg: "#ECFDF5", outline: "1px dashed #6EE7B7", color: "#065F46" };
  }
  return { bg: "#ECFDF5", outline: "none", color: "#065F46" };
}

export function ScheduleBuilderGridLegend({ lang, open, onToggle }) {
  const t = (en, gr) => (lang === "gr" ? gr : en);
  const items = [
    { swatch: { background: "#ECFDF5" }, label: t("Unsaved", "Μη αποθηκευμένο") },
    { swatch: { background: "#FFF7ED", border: "2px dashed #EA580C" }, label: t("Updated elsewhere", "Ενημερώθηκε αλλού") },
    { swatch: { background: "#ECFDF5", border: "1px dashed #6EE7B7" }, label: t("Draft", "Προσχέδιο") },
    { swatch: { background: "#D1FAE5", border: "2px solid #15803D" }, label: t("Published (Internal, not SoftOne)", "Δημοσιευμένο (Εσωτερικό, όχι SoftOne)") },
    { swatch: { background: "#FEF9C3", border: "2px solid #F59E0B" }, label: t("Needs re-publishing", "Χρειάζεται επαναδημοσίευση") },
    { swatch: { background: "#FBBF24" }, label: t("Rest day", "Ρεπό") },
    { swatch: { background: "#22C55E" }, label: t("Annual leave", "Άδεια") },
    { swatch: { background: "#F97316" }, label: t("Sick leave", "Αναρρωτική") },
    { swatch: { background: "#FEE2E2", border: "2px solid #EF4444" }, label: t("Rest law violation", "Παράβαση ανάπαυσης") },
    { swatch: { background: "#FEF3C7", border: "1.5px dashed #F59E0B" }, label: t("Loan pending", "Δανεισμός εκκρεμεί") },
    { swatch: { background: "#CCFBF1", border: "1.5px solid #0D9488" }, label: t("Loan confirmed", "Δανεισμός επιβεβ.") },
    { swatch: { background: "#D1FAE5", border: "1px solid #34D399" }, label: t("S1✓  Matches SoftOne", "S1✓  Ταιριάζει με SoftOne") },
    { swatch: { background: "#FEF3C7", border: "1px solid #F59E0B" }, label: t("S1✗  Differs from SoftOne", "S1✗  Διαφέρει από το SoftOne") },
    { swatch: { background: "#E0E7FF", border: "1px solid #6366F1" }, label: t("ER✓  Matches Ergani", "ER✓  Ταιριάζει με Εργάνη") },
    { swatch: { background: "#fff", border: "1px solid #A5B4FC" }, label: t("ER✗  Differs from Ergani", "ER✗  Διαφέρει από το Εργάνη") },
    { swatch: { background: "transparent" }, label: t("Internal, SoftOne and Ergani can all differ", "Εσωτερικό, SoftOne και Εργάνη μπορεί να διαφέρουν") },
  ];

  return (
    <div style={{ marginBottom: 10 }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "4px 10px",
          fontSize: 11,
          fontWeight: 700,
          color: "var(--text-muted)",
          background: "var(--surface2)",
          border: "1px solid var(--border)",
          borderRadius: 8,
          cursor: "pointer",
          fontFamily: "DM Sans, sans-serif",
        }}
      >
        <span
          aria-hidden
          style={{
            width: 18,
            height: 18,
            borderRadius: "50%",
            background: "var(--surface)",
            border: "1px solid var(--border)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 11,
            fontWeight: 800,
            color: "var(--text-secondary)",
          }}
        >
          ?
        </span>
        {t("Legend", "Υπόμνημα")}
        <span aria-hidden style={{ fontSize: 9, opacity: 0.7 }}>{open ? "▲" : "▼"}</span>
      </button>
      {open ? (
        <div className="sb-legend" role="list">
          {items.map((item) => (
            <div key={item.label} className="sb-legend-item" role="listitem">
              <div
                className="sb-legend-swatch"
                style={{
                  background: item.swatch.background,
                  border: item.swatch.border || "none",
                }}
              >
              </div>
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}


